import React, { useEffect, useState } from 'react';
import { Stack, StackItem, Text, TooltipHost } from '@fluentui/react';
import { IC } from 'components/SVGIcon';
import { Desk, RoomTable } from 'kits/IconKit2';
import IvicosStrings from 'kits/language/stringKit';
import { IRoomResource } from 'kits/apiKit3/legacy';
import { iconButtonStylesDefault, iconButtonStylesSelected } from '../CallActionBar.style';
import {
    useIsMeetingCornerSettingLoadedFromDB,
    useIsUserAloneInOwnPersonalRoom,
    useMeetingCornerSetting
} from 'features/Rooms/PersonalMeetingCorner/states/hooks';
import { idpService } from 'services/api/identity-provider.service';
import { useWindowWidth } from 'shared-state/display-options/hooks';
import { redSave } from 'features/Rooms/PersonalMeetingCorner/assets/icons';

interface Props {
    currentRoomId?: string;
    currentRoom?: IRoomResource;
    isVisitorAtLandingPage: boolean;
    typeOfPersonalRoomTable: 'desk' | 'meeting-table';
    setTypeOfPersonalRoomTable: (val: 'desk' | 'meeting-table') => void;
}

const RoomHeader: React.FC<Props> = ({ currentRoomId, currentRoom, isVisitorAtLandingPage, typeOfPersonalRoomTable, setTypeOfPersonalRoomTable }) => {
    const { widgets, topImageIndex, bottomImageIndex } = useMeetingCornerSetting();
    const [isMeetingCornerSettingLoadedFromDB] = useIsMeetingCornerSettingLoadedFromDB();
    const isUserAloneInOwnPersonalRoom = useIsUserAloneInOwnPersonalRoom();
    const { smallDevice, mediumDevice } = useWindowWidth();
    const [hasSettingsChanged, setHasSettingsChanged] = useState(false);
    const [originalValues, setOriginalValues] = useState<{
        widgets: any[];
        topImageIndex: number;
        bottomImageIndex: number;
    } | null>(null);

    useEffect(() => {
        // Set original values on first load
        if (originalValues === null && isMeetingCornerSettingLoadedFromDB) {
            setOriginalValues({
                widgets: [...widgets],
                topImageIndex,
                bottomImageIndex
            });
            setHasSettingsChanged(false);
            return;
        }

        // Compare current values with original values
        if (originalValues !== null) {
            const hasChanged =
                topImageIndex !== originalValues.topImageIndex ||
                bottomImageIndex !== originalValues.bottomImageIndex ||
                widgets.length !== originalValues.widgets.length ||
                JSON.stringify(widgets) !== JSON.stringify(originalValues.widgets);

            setHasSettingsChanged(hasChanged);
        }
    }, [topImageIndex, bottomImageIndex, widgets, originalValues, isMeetingCornerSettingLoadedFromDB]);

    const updateMeetingCornerSetting = async () => {
        if (isUserAloneInOwnPersonalRoom) {
            await idpService.updateUser(undefined, undefined, undefined, undefined, undefined, undefined, {
                room_id: currentRoomId,
                top_image_index: topImageIndex,
                bottom_image_index: bottomImageIndex,
                widgets_list: widgets
            });
        }

        return;
    };

    if (currentRoomId?.includes('personal')) {
        if (smallDevice || mediumDevice) {
            return (
                <Stack horizontal verticalAlign="center" horizontalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Text variant="medium">{currentRoom?.attributes.displayName}</Text>
                </Stack>
            );
        }
        return (
            <Stack horizontal verticalAlign="center" horizontalAlign="center" tokens={{ childrenGap: 8 }}>
                <TooltipHost
                    content={
                        hasSettingsChanged
                            ? IvicosStrings.personalMeetingCornerStrings.tooltips.main.saveChanges
                            : IvicosStrings.personalMeetingCornerStrings.tooltips.main.personalDashboard
                    }
                >
                    <StackItem
                        onClick={async () => {
                            if (hasSettingsChanged) {
                                // Save changes and update original values
                                await updateMeetingCornerSetting();
                                setOriginalValues({
                                    widgets: [...widgets],
                                    topImageIndex,
                                    bottomImageIndex
                                });
                            }
                            setTypeOfPersonalRoomTable('desk');
                        }}
                        className={
                            typeOfPersonalRoomTable === 'desk'
                                ? iconButtonStylesSelected
                                : hasSettingsChanged
                                ? iconButtonStylesDefault(true)
                                : iconButtonStylesDefault(false)
                        }
                    >
                        {hasSettingsChanged && isUserAloneInOwnPersonalRoom ? (
                            <img src={redSave} alt="save" style={{ width: 25, height: 25 }} />
                        ) : (
                            <IC variant="dark" size={64}>
                                <Desk />
                            </IC>
                        )}
                    </StackItem>
                </TooltipHost>
                <TooltipHost content={IvicosStrings.personalMeetingCornerStrings.tooltips.main.personalMeetingCorner}>
                    <StackItem
                        onClick={() => setTypeOfPersonalRoomTable('meeting-table')}
                        className={typeOfPersonalRoomTable === 'meeting-table' ? iconButtonStylesSelected : iconButtonStylesDefault(false)}
                    >
                        <IC variant="dark" size={64}>
                            <RoomTable />
                        </IC>
                    </StackItem>
                </TooltipHost>
            </Stack>
            // <Stack horizontal verticalAlign="center" horizontalAlign="center" tokens={{ childrenGap: 8 }}>
            //     <TooltipHost
            //         content={
            //             hasSettingsChanged
            //                 ? IvicosStrings.personalMeetingCornerStrings.tooltips.main.saveChanges
            //                 : IvicosStrings.personalMeetingCornerStrings.tooltips.main.personalDashboard
            //         }
            //     >
            //         <StackItem
            //             onClick={async () => {
            //                 if (hasSettingsChanged) {
            //                     // Save changes and update original values
            //                     await updateMeetingCornerSetting();
            //                     setOriginalValues({
            //                         widgets: [...widgets],
            //                         topImageIndex,
            //                         bottomImageIndex
            //                     });
            //                 } else {
            //                     // Switch to desk view
            //                     setTypeOfPersonalRoomTable('desk');
            //                 }
            //             }}
            //             className={typeOfPersonalRoomTable === 'desk' ? iconButtonStylesSelected : iconButtonStylesDefault}
            //         >
            //             {hasSettingsChanged ? (
            //                 <img src={redSave} alt="save" style={{ width: 30, height: 30 }} />
            //             ) : (
            //                 <IC variant="dark" size={64}>
            //                     <Desk />
            //                 </IC>
            //             )}
            //         </StackItem>
            //     </TooltipHost>
            //     <TooltipHost content={IvicosStrings.personalMeetingCornerStrings.tooltips.main.personalMeetingCorner}>
            //         <StackItem
            //             onClick={() => setTypeOfPersonalRoomTable('meeting-table')}
            //             className={typeOfPersonalRoomTable === 'meeting-table' ? iconButtonStylesSelected : iconButtonStylesDefault}
            //         >
            //             <IC variant="dark" size={64}>
            //                 <RoomTable />
            //             </IC>
            //         </StackItem>
            //     </TooltipHost>
            // </Stack>
        );
    }

    if (currentRoomId?.includes('visitor')) {
        return !isVisitorAtLandingPage ? <>{IvicosStrings.waitingAreaTitle}</> : null;
    }

    return (
        <Text
            variant="medium"
            style={{
                maxWidth: smallDevice || mediumDevice ? '100%' : 160,
                wordBreak: 'break-word'
            }}
        >
            {currentRoom?.attributes.displayName ?? 'Video call'}
        </Text>
    );
};

export default RoomHeader;
